import { useEffect, useRef, useState } from 'react';
import { fetchCarsByPlate } from '../api/carsApi';
import { notify } from '../shared/feedback/notify';

const PLATE_LENGTH = 7;

interface UsePlateCheckOptions {
  /** Habilita a checagem (ex: modal aberto). */
  enabled: boolean;
  /** Placa digitada (qualquer formato — é normalizada internamente). */
  plate: string;
  /** Chamado quando todas as visitas anteriores estão encerradas — recebe a mais recente para pré-preencher. */
  onHistoryFound: (mostRecent: any) => void;
}

/**
 * Checa a placa digitada contra o histórico: bloqueia o cadastro se houver
 * atendimento ativo e dispara pré-preenchimento quando há visitas encerradas.
 */
export function usePlateCheck({ enabled, plate, onHistoryFound }: UsePlateCheckOptions) {
  const [isChecking, setIsChecking] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [foundInHistory, setFoundInHistory] = useState(false);
  const [activeVisitError, setActiveVisitError] = useState<string | null>(null);
  const onHistoryFoundRef = useRef(onHistoryFound);
  onHistoryFoundRef.current = onHistoryFound;

  const reset = () => {
    setIsBlocked(false);
    setFoundInHistory(false);
    setActiveVisitError(null);
  };

  useEffect(() => {
    if (!enabled) {
      reset();
      return;
    }

    const normalizedPlate = plate.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    if (normalizedPlate.length !== PLATE_LENGTH) {
      reset();
      return;
    }

    let isCurrent = true;

    const checkPlate = async () => {
      setIsChecking(true);
      setActiveVisitError(null);
      try {
        const history = await fetchCarsByPlate(normalizedPlate);
        if (!isCurrent) return;

        if (history && history.length > 0) {
          const activeVisit = history.find((visit: any) => visit.status !== 'ENTREGA');

          if (activeVisit) {
            setIsBlocked(true);
            setFoundInHistory(false);
            const statusLabel = activeVisit.statusLabel || activeVisit.status;
            setActiveVisitError(`Este veículo já está em atendimento ativo na oficina (Status: ${statusLabel}).`);
          } else {
            setIsBlocked(false);
            setFoundInHistory(true);
            onHistoryFoundRef.current(history[0]);
          }
        } else {
          setIsBlocked(false);
          setFoundInHistory(false);
        }
      } catch {
        // A falha não bloqueia o formulário — o backend revalida a placa no submit —,
        // mas o usuário precisa saber que a verificação de atendimento ativo e o
        // pré-preenchimento por visita anterior não rodaram. toastId fixo porque a
        // checagem é redisparada a cada placa digitada.
        if (isCurrent) {
          notify.warning(
            'Não foi possível verificar o histórico desta placa. O cadastro continua disponível e os dados serão validados ao salvar.',
            { toastId: 'placa-check-indisponivel' }
          );
        }
      } finally {
        if (isCurrent) setIsChecking(false);
      }
    };

    void checkPlate();

    return () => {
      isCurrent = false;
    };
  }, [enabled, plate]);

  return { isChecking, isBlocked, foundInHistory, activeVisitError };
}
